import React, { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react'
import type { CalendarItem } from '@fs/zion-rootstech'
import useScheduleData from '../../components/schedule/hooks/useScheduleData'
import { useDetailAugmentation } from '../../hooks'

interface CalendarDataContextValue {
  calendarData: CalendarItem[]
  loading: boolean
}

const CalendarDataContext = createContext<CalendarDataContextValue | undefined>(undefined)

export function useCalendarDataContext(): CalendarDataContextValue {
  const context = useContext(CalendarDataContext)
  if (!context) {
    throw new Error('useCalendarDataContext must be used within CalendarDataProvider')
  }
  return context
}

/** Registers one consumer of the calendar fetch and returns that consumer's de-registration. */
type RegisterCalendarConsumer = () => () => void

// Defaults to throwing rather than to a no-op: outside the provider a no-op would leave the calendar
// permanently empty, which reads downstream as a short schedule rather than as a wiring mistake.
const CalendarActivationContext = createContext<RegisterCalendarConsumer>(() => {
  throw new Error('useActivateCalendarData must be used within CalendarDataProvider')
})

/**
 * Turns the calendar fetch on for as long as the calling component stays mounted.
 *
 * The fetch is opt-in so that the question of when the calendar is needed is answered by the component
 * that reads it, rather than restated as a route or experience-phase test in the provider — see
 * CalendarDataProvider below.
 */
export function useActivateCalendarData(): void {
  const register = useContext(CalendarActivationContext)
  useEffect(() => register(), [register])
}

interface CalendarDataProviderProps {
  children: ReactNode
}

/**
 * CalendarDataProvider
 *
 * Single source of truth for calendar data.
 * Fetches calendar data and provides it via context to prevent reference instability.
 *
 * Responsibilities:
 * - Fetch calendar data from multiple sources (useScheduleData), but only while a consumer has
 *   activated it via useActivateCalendarData
 * - Augment with app-specific data (useDetailAugmentation)
 * - Transform to CalendarItem[] format
 * - Provide stable references via proper memoization
 * - Expose via context (not props) to consumers
 *
 * This provider uses app-specific hooks:
 * - useScheduleData: App's calendar fetching with feature flags
 * - useDetailAugmentation: App's playlist/watch history augmentation
 *
 * These hooks are also used elsewhere in the app (search, sessions, speakers, etc.),
 * so they belong in the app layer, not a shared library.
 */
export default function CalendarDataProvider({ children }: CalendarDataProviderProps): JSX.Element {
  // This provider is mounted app-wide because MyScheduleProvider is, but the fetch only runs while
  // something that reads calendar-derived state is mounted and has asked for it. Today that is exactly
  // ScheduleProvider, which the two schedule routes mount (ScheduleRoot/MyScheduleRoot). Everything
  // downstream is schedule-scoped: ScheduleCard renders solely via ExpandableSchedule ->
  // ScheduleItemExpander -> FullSchedule (/schedule) or MySchedule (/user/schedule), and every
  // useScheduleContext consumer lives under those pages. Empty calendar data is inert elsewhere —
  // zion's consolidation bails on `!transformedScheduleData.length` (so it never writes back a
  // hollowed-out schedule) and its grouping effect is length-guarded, while `mySchedule` itself comes
  // from the user API rather than from here, so the saved-session count and migration banner still
  // work app-wide.
  //
  // Keyed to the consumer rather than to a route match because '/schedule' renders one of three pages
  // depending on the experience phase and only one of them is the schedule: the class preview fetches
  // through useClassPreview and the archived page fetches nothing, so a route test bought the calendar
  // — and, for a signed-in visitor, the playlist and watch-history requests behind it — for two pages
  // that read none of it. Asking the consumer also keeps the phase table ScheduleRoot owns from being
  // restated here, where a copy could silently disagree with what rendered.
  //
  // Counted rather than a boolean so overlapping consumers can't have the first one's unmount switch
  // the fetch off underneath the second.
  const [consumerCount, setConsumerCount] = useState(0)
  const registerConsumer = useCallback<RegisterCalendarConsumer>(() => {
    setConsumerCount((count) => count + 1)
    return () => setConsumerCount((count) => count - 1)
  }, [])

  // Fetch calendar data from GraphQL (multi-source with feature flags)
  const { loading: scheduleLoading, data } = useScheduleData({ skip: consumerCount === 0 })

  // Transform for augmentation (expects items with date at top level)
  const transformedData = useMemo(() => {
    if (!data) return []
    return data.map(({ date, item }) => ({ ...item, date }))
  }, [data])

  // Augment with watch history and playlists
  // This hook is used app-wide, not just for calendar
  const { data: augmentedData, loading: augmentationLoading } = useDetailAugmentation({
    loading: scheduleLoading,
    data: transformedData,
  })

  // Format as CalendarItem[] for Zion providers
  // CRITICAL: Final memoization ensures stable references
  const calendarData = useMemo(() => {
    if (!augmentedData) return []
    return augmentedData.map((item) => ({
      date: item.date,
      item,
    }))
  }, [augmentedData])

  const loading = scheduleLoading || augmentationLoading

  // Memoize context value to prevent unnecessary re-renders
  const value = useMemo<CalendarDataContextValue>(
    () => ({
      calendarData,
      loading,
    }),
    [calendarData, loading]
  )

  return (
    <CalendarActivationContext.Provider value={registerConsumer}>
      <CalendarDataContext.Provider value={value}>{children}</CalendarDataContext.Provider>
    </CalendarActivationContext.Provider>
  )
}
